import { safeFrom, safeAuth, safeStorage, isSupabaseAvailable } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError,
  notFoundError,
  notConfiguredError
} from '@/lib/service-errors';
import { Page, PageVersion, PageFormValues, PageContentBlock } from '@/types/page';
import { auditContent } from '@/lib/audit-logger';

// Type for the page data we get from Supabase
interface PageRow {
  id: string;
  slug: string;
  title: string;
  description?: string | null;
  content: any;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords?: string[] | null;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;
}

// Type for the page data we'll return from our service
type ProcessedPage = Omit<PageRow, 'content'> & {
  content: Page['content'];
};

export const pageService = {
  // Get all pages (published only for public, all for admins)
  async getPages({ isAdmin = false }: { isAdmin?: boolean } = {}): Promise<ServiceResponse<Page[]>> {
    if (!isSupabaseAvailable()) {
      return successResponse([]);
    }
    
    try {
      let query = safeFrom<PageRow>('pages')
        .select('*')
        .order('title', { ascending: true });

      if (!isAdmin) {
        query = query.eq('is_published', true) as any;
      }

      const { data, error } = await query;
      
      if (error) {
        throw error;
      }
      
      // Process each page's content and convert ID to string
      const pages = (data || []).map((page: any): Page => ({
        ...page,
        id: String(page.id), // Convert bigserial to string
        content: this.processPageContent(page.content)
      }));
      
      return successResponse(pages);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'pageService.getPages'));
    }
  },

  // Process page content to ensure it has the correct structure
  processPageContent(content: unknown): Page['content'] {
    const defaultContent: Page['content'] = { blocks: [], version: '2.28.1' };
    
    if (!content) return defaultContent;
    
    try {
      // If content is a string, try to parse it as JSON
      const parsedContent = typeof content === 'string'
        ? JSON.parse(content)
        : content;
      
      // If it's not an object, return default
      if (!parsedContent || typeof parsedContent !== 'object') {
        return defaultContent;
      }
      
      // If it's already in Editor.js format, return as-is
      if (Array.isArray(parsedContent.blocks)) {
        return {
          blocks: parsedContent.blocks,
          version: typeof parsedContent.version === 'string' ? parsedContent.version : '2.28.1'
        };
      }
      
      // CRITICAL FIX: Handle content that exists but doesn't have Editor.js blocks structure
      // Convert various content formats to Editor.js format to prevent data loss
      
      // If content has time property, it might be Editor.js with different structure
      if (parsedContent.time || parsedContent.version) {
        // Try to extract blocks even if structure is different
        const blocks = parsedContent.blocks || [];
        return {
          blocks: Array.isArray(blocks) ? blocks : [],
          version: parsedContent.version || '2.28.1'
        };
      }
      
      // If content is a simple object with text properties, convert to paragraph
      if (typeof parsedContent === 'object') {
        // Try to convert object properties to blocks
        const text = parsedContent.text || parsedContent.content || JSON.stringify(parsedContent);
        return {
          blocks: [
            {
              type: 'paragraph',
              data: {
                text: text
              }
            }
          ],
          version: '2.28.1'
        };
      }
      
      return defaultContent;
      
    } catch (error) {
      console.error('Error processing page content:', error);
      // If JSON parsing fails but content exists, try to preserve original content as text
      if (content && typeof content === 'string') {
        // Return a simple structure with the content as a paragraph block
        return {
          blocks: [
            {
              type: 'paragraph',
              data: {
                text: content
              }
            }
          ],
          version: '2.28.1'
        };
      }
      return defaultContent;
    }
  },

  // Get a single page by slug
  async getPageBySlug(slug: string, { isAdmin = false }: { isAdmin?: boolean } = {}): Promise<ServiceResponse<Page | null>> {
    if (!isSupabaseAvailable()) {
      return successResponse(null);
    }
    
    try {
      let query = safeFrom<PageRow>('pages')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (!isAdmin) {
        query = query.eq('is_published', true) as any;
      }

      const { data, error } = await query;
      
      if (error) {
        // PGRST116 = no rows returned, not an error
        if (error.code === 'PGRST116') {
          return successResponse(null);
        }
        throw error;
      }
      
      if (!data) {
        return successResponse(null);
      }
      
      const page: Page = {
        ...data,
        id: String(data.id), // Convert bigserial to string
        content: this.processPageContent(data.content)
      };
      
      return successResponse(page);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'pageService.getPageBySlug', { slug }));
    }
  },

  // Create a new page
  async createPage(pageData: Omit<PageFormValues, 'id'>): Promise<ServiceResponse<Page>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const { user } = await safeAuth.getUser();

      const { data, error } = await safeFrom('pages')
        .insert([{
          ...pageData,
          created_by: user?.id
        }])
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      const page: Page = {
        ...data,
        id: String(data.id), // Convert bigserial to string
        content: this.processPageContent(data.content)
      };
      
      // Audit log
      await auditContent.create('page', page.id, {
        title: page.title,
        slug: page.slug,
      });
      
      return successResponse(page);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'pageService.createPage'));
    }
  },

  // Update an existing page
  async updatePage(id: string, pageData: Partial<PageFormValues>): Promise<ServiceResponse<Page>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const { user } = await safeAuth.getUser();

      // Convert string ID to number (database uses bigserial)
      const pageId = typeof id === 'string' ? parseInt(id, 10) : id;
      if (isNaN(pageId)) {
        throw new Error(`Invalid page ID: ${id}`);
      }

      // Prepare update payload - only include valid fields
      const updatePayload: any = {};

      // Only set updated_by if user is available
      if (user?.id) {
        updatePayload.updated_by = user.id;
      }

      // Only include fields that are provided and valid
      if (pageData.title !== undefined) updatePayload.title = pageData.title.trim();
      if (pageData.slug !== undefined) updatePayload.slug = pageData.slug.trim();
      if (pageData.description !== undefined) updatePayload.description = pageData.description?.trim() || null;
      if (pageData.is_published !== undefined) updatePayload.is_published = pageData.is_published;
      if (pageData.seo_title !== undefined) updatePayload.seo_title = pageData.seo_title?.trim() || null;
      if (pageData.seo_description !== undefined) updatePayload.seo_description = pageData.seo_description?.trim() || null;
      if (pageData.seo_keywords !== undefined) {
        // Ensure seo_keywords is an array or null
        updatePayload.seo_keywords = Array.isArray(pageData.seo_keywords) 
          ? pageData.seo_keywords.filter(k => k && k.trim()).map(k => k.trim())
          : null;
      }
      
      // Handle content - ensure it's a valid JSON object for jsonb column
      if (pageData.content !== undefined) {
        if (typeof pageData.content === 'object' && pageData.content !== null) {
          updatePayload.content = pageData.content;
        } else {
          // If content is a string, try to parse it
          try {
            updatePayload.content = typeof pageData.content === 'string' 
              ? JSON.parse(pageData.content) 
              : pageData.content;
          } catch {
            // If parsing fails, use default structure
            updatePayload.content = { blocks: [], version: '2.28.1' };
          }
        }
      }

      // Ensure we have at least one field to update (besides updated_by)
      const fieldsToUpdate = Object.keys(updatePayload).filter(key => key !== 'updated_by');
      if (fieldsToUpdate.length === 0) {
        return errorResponse(handleServiceError(
          new Error('No fields provided to update'),
          'pageService.updatePage',
          { id }
        ));
      }

      const { data, error } = await safeFrom('pages')
        .update(updatePayload)
        .eq('id', pageId)
        .select()
        .single();
      
      if (error) {
        console.error('[pageService.updatePage] Supabase error:', {
          error,
          errorCode: (error as any).code,
          errorMessage: (error as any).message,
          errorDetails: (error as any).details,
          errorHint: (error as any).hint,
          pageId,
          pageIdType: typeof pageId,
          updatePayload,
          payloadKeys: Object.keys(updatePayload)
        });
        throw error;
      }
      
      const page: Page = {
        ...data,
        id: String(data.id), // Convert back to string for consistency
        content: this.processPageContent(data.content)
      };
      
      // Get old data for audit log (simplified - in production, fetch before update)
      await auditContent.update('page', String(pageId), {}, {
        title: page.title,
        slug: page.slug,
        updated_at: page.updated_at,
      });
      
      return successResponse(page);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'pageService.updatePage', { id }));
    }
  },

  // Delete a page
  async deletePage(id: string): Promise<ServiceResponse<boolean>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      // Convert string ID to number (database uses bigserial)
      const pageId = typeof id === 'string' ? parseInt(id, 10) : id;
      if (isNaN(pageId)) {
        throw new Error(`Invalid page ID: ${id}`);
      }

      const { error } = await safeFrom('pages')
        .delete()
        .eq('id', pageId);
      
      if (error) {
        throw error;
      }
      
      // Audit log (simplified - in production, fetch before delete)
      await auditContent.delete('page', id, { id });
      
      return successResponse(true);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'pageService.deletePage', { id }));
    }
  },

  // Get page versions
  async getPageVersions(pageId: string): Promise<ServiceResponse<PageVersion[]>> {
    if (!isSupabaseAvailable()) {
      return successResponse([]);
    }

    try {
      const { data, error } = await safeFrom('page_versions')
        .select('*')
        .eq('page_id', pageId)
        .order('version_number', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      return successResponse((data || []) as PageVersion[]);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'pageService.getPageVersions', { pageId }));
    }
  },

  // Note: Page media functions removed - images are now embedded directly in content via rich text editor
  // This eliminates the need for a separate page_media table and associated CRUD operations
};
